import { Layout } from "antd";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FanPaymentVerify } from "../../../components/FanPaymentVerifyComponent/FanPaymentVerify";
import LiveRTMP from "../../../components/LivePublisherComponent/LiveRTMP";
import LiveSubscriberComponent from "../../../components/LiveSubscriberComponent";
import OptionsModalComponent from "../../../components/OptionsModalComponent";
import ReportModal from "../../../components/ReportModal";
import ShareModalComponent from "../../../components/ShareModalComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import AgoraProvider from "../../../helpers/agoraProvider";
import { USER_TYPES } from "../../../helpers/constant";
import { addLiveComment, clearStreamStats, newLiveTip, updateStreamStats } from "../../../redux/lives/liveDataSlice";
import { getLiveById, updateEndLive } from "../../../redux/lives/livesSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { liveService } from "../../../services";
import { getSocket, offSocketReconnected, onSocketReconnected } from "../../../socket";
import { FanLiveFooter } from "./FanLiveFooter";
import { FanLiveHeader } from "./FanLiveHeader";
import { FanLiveRTMPFooter } from "./FanLiveRTMPFooter";
import { FanLiveRTMPHeader } from "./FanLiveRTMPHeader";
import "./index.less";

const FanLivePage = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const location = useLocation();
  const subscriberRef = useRef<any>(null);
  const joinedRef = useRef<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const live = useSelector((state: StoreState) => state.lives.item);
  //const token = useSelector((state: StoreState) => state.lives.token);
  const [token, setToken] = useState("");
  const isLoading = useSelector((state: StoreState) => state.lives.loading);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const searchParams = new URLSearchParams(location.search);
  const fromPaymentPage = searchParams.get("from");

  useEffect(() => {
    if (!id) return;
    dispatch(getLiveById(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (!live || !live.sessionId || !live.isPaymentVerified || joinedRef.current) return;
    const getToken = async (sessionId: string) => {
      await liveService.buildToken(sessionId, live.isRTMP).then((responseToken) => {
        if (responseToken && typeof responseToken === "string") {
          setToken(responseToken);
        }
      });
    };
    getToken(live.sessionId);
  }, [live, id, dispatch]);

  useEffect(() => {
    if (token) {
      const joinLive = async () => {
        if (!token) return;
        subscriberRef.current && (await subscriberRef.current.join(token));
        const socket = getSocket();
        socket.emit("public-stream/join", { id });
      };
      joinLive();
    }
  }, [token]);

  const leaveLive = async () => {
    subscriberRef.current && (await subscriberRef.current.leave());
    const socket = getSocket();
    socket.emit("public-stream/leave", { id, role: USER_TYPES.FAN });
  };

  const onStreamStatusChanged = (value: boolean) => {
    joinedRef.current = value;
  };

  const unSubscribed = async (streamingTime: number) => {
    const socket = getSocket();
    socket.emit("public-stream/leave", { id, streamingTime, role: USER_TYPES.CREATOR });
  };

  const userJoined = async (data: any) => {
    if (data.streamStats) await dispatch(updateStreamStats(data.streamStats));
    if (data.comments) await dispatch(addLiveComment(data.comments));
    if (data.lastStreamTip) await dispatch(newLiveTip(data.lastStreamTip));
  };

  const userLeaved = async (data: any) => {
    if (data?.streamStats && !data?.isStreamingEnds) {
      await dispatch(updateStreamStats(data?.streamStats));
    }
    if (data?.isStreamingEnds) {
      dispatch(updateEndLive({ streamId: data?.streamId, isStreamingEnds: data?.isStreamingEnds }));
      await dispatch(clearStreamStats());
      navigate("/p/fan/lives");
    }

    // if(live && live._id) {
    //   console.log('livelive insideeeeeeeeeeeeeeeeeeeeeeee');
    //   if (data?.streamStats && !data?.isStreamingEnds && data.streamId === live?._id) {
    //     console.log('livelive user leaving live id ', live._id, ' leaving live id ', data.streamId);

    //     await dispatch(updateStreamStats(data?.streamStats));
    //   }
    //   if (data?.isStreamingEnds && data.streamId === live?._id) {
    //     console.log('livelive ending leaving live id ', live._id, ' leaving live id ', data.streamId);

    //     await dispatch(clearStreamStats());
    //     navigate("/p/fan/lives");
    //   }
    // }
  };

  useEffect(() => {
    const registerLiveEventListeners = () => {
      const socket = getSocket();
      // Remove existing listeners first to avoid duplicates
      socket.off("user_joined", userJoined);
      socket.off("user_leaved", userLeaved);

      // Register listeners
      socket.on("user_joined", userJoined);
      socket.on("user_leaved", userLeaved);
    };

    const unregisterLiveEventListeners = () => {
      const socket = getSocket();
      socket.off("user_joined", userJoined);
      socket.off("user_leaved", userLeaved);
    };

    // Handler for socket reconnection - re-register listeners AND re-join stream room
    const handleSocketReconnected = (event: CustomEvent) => {
      console.log("🔄 Socket reconnected in FanLivePage, re-registering listeners for socket ID:", event.detail.socketId);
      registerLiveEventListeners();

      // Re-join the stream room after socket reconnection
      if (joinedRef.current && id) {
        const socket = getSocket();
        console.log("🔄 Re-joining stream room after socket reconnection:", id);
        socket.emit("public-stream/join", { id });
      }
    };

    // Register listeners initially
    registerLiveEventListeners();

    // Listen for socket reconnection events
    onSocketReconnected(handleSocketReconnected);

    return () => {
      unregisterLiveEventListeners();
      offSocketReconnected(handleSocketReconnected);

      if (joinedRef.current) {
        leaveLive();
      }
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (live && !live?.isStreaming && live?.isEndStreaming) {
      if (fromPaymentPage) {
        if (fromPaymentPage === "mail") {
          navigate("/fan/home");
        } else {
          navigate("/fan/lives?tab=now");
        }
      } else {
        navigate("/fan/home");
      }
    }
  }, [live]);

  const closeReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const closeOptionsModal = useCallback(() => {
    setIsOptionsModalOpen(false);
  }, []);

  const onReportClick = useCallback(() => {
    setIsOptionsModalOpen(false);
    setIsReportModalOpen(true);
  }, []);

  const onOptionsClick = useCallback(() => {
    setIsOptionsModalOpen(true);
  }, []);

  const onShareClick = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const closeShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  return (
    <AgoraProvider config={{ mode: "live", codec: "h264", role: "audience" }}>
      <Layout>
        <Layout.Content className="fanLiveContentWrapper">
          {isLoading ? (
            <SpinnerComponent />
          ) : (
            live?.isPaymentVerified &&
            (live?.isRTMP ? (
              <>
                <FanLiveRTMPHeader live={live} />
                <LiveRTMP
                  sessionId={live?.sessionId || null}
                  remoteId={live?.creator?._id || null}
                  ref={subscriberRef}
                  onStreamStatusChange={onStreamStatusChanged}
                  unSubscribed={unSubscribed}
                  isFromMobile={false}
                />
                <FanLiveRTMPFooter id={id!} live={live} />
              </>
            ) : (
              <>
                <FanLiveHeader live={live} isSubscribed={live?.isSubscribed || false} onOptionsClick={onOptionsClick} onShareClick={onShareClick} />
                <LiveSubscriberComponent
                  sessionId={live?.sessionId || null}
                  remoteId={live?.creator?._id || null}
                  ref={subscriberRef}
                  onStreamStatusChange={onStreamStatusChanged}
                  unSubscribed={unSubscribed}
                  isFromMobile={live.isFromMobile!}
                />
                <FanLiveFooter id={id!} isChatAvailable={live?.isChatAll || (live?.isChatSubscribers && live?.isSubscribed) || false} live={live} />
              </>
            ))
          )}
          {live && <FanPaymentVerify live={live} />}
          <OptionsModalComponent
            reportBtnLabel={t("report-live")}
            isOpen={isOptionsModalOpen}
            onClose={closeOptionsModal}
            onReportClick={onReportClick}
          />
          <ReportModal isOpen={isReportModalOpen} target="live" targetId={live?._id!} onClose={closeReportModal} />
          <ShareModalComponent
            isOpen={isShareModalOpen}
            onClose={closeShareModal}
            id={live?.creator?._id}
            username={live?.creator?.username}
            link={`${window.location.origin}/fan/live/${live?._id}`}
          />
        </Layout.Content>
      </Layout>
    </AgoraProvider>
  );
};

export default FanLivePage;
